import React from 'react';
import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { truncOneDecimal } from 'utils/helpers';

const styles = StyleSheet.create({
  section: {
    marginTop: 2,
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    color: '#18578f',
    marginBottom: 8,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  card: {
    width: '48.5%',
    minHeight: 70,
    borderWidth: 1,
    borderColor: '#d9e1ef',
    paddingTop: 9,
    paddingRight: 10,
    paddingBottom: 9,
    paddingLeft: 10,
    marginBottom: 8,
  },
  fullCard: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#d9e1ef',
    paddingTop: 9,
    paddingRight: 10,
    paddingBottom: 9,
    paddingLeft: 10,
    marginBottom: 8,
  },
  label: {
    fontSize: 8.5,
    fontWeight: 700,
    color: '#7189ad',
    marginBottom: 6,
  },
  value: {
    fontSize: 20,
    fontWeight: 700,
    color: '#18578f',
    lineHeight: 1.1,
  },
  note: {
    fontSize: 8.5,
    color: '#17345c',
    marginTop: 4,
  },
  chartTitle: {
    paddingTop: 8,
    textAlign: 'center',
    color: '#7189ad',
    fontSize: 8.5,
  },
  chart: {
    width: 230,
    marginHorizontal: 120,
    marginTop: 6,
  },
  sub: {
    verticalAlign: 'sub',
    fontSize: 7,
  },
});

const formatDecimal = (value, decimals = 1) => Number(value).toFixed(decimals).replace('.', ',');
const formatInteger = (value) => Number(Math.round(value)).toLocaleString('es-ES');

const KpiCard = ({ label, value, note, full = false }) => (
  <View style={full ? styles.fullCard : styles.card} wrap={false}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value}</Text>
    {note ? <Text style={styles.note}>{note}</Text> : null}
  </View>
);

const KPIPdf = ({ mode, currentScenario, base, newValue, kpiDict = {}, año }) => {
  if (mode === 'emisiones_netas') {
    const amount = Number(newValue.toFixed(1));
    const perCapita = Number((newValue / 18).toFixed(1));

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          <KpiCard
            label={`Emisiones netas totales en ${año}`}
            value={`${formatDecimal(amount)} millones tCO2e`}
          />
          <KpiCard
            label="Carbono neutralidad"
            value={amount > 0 ? 'No se cumple' : 'Sí se cumple'}
            note={`Evaluación al año ${año}.`}
          />
          <KpiCard
            label="Emisiones netas per cápita"
            value={`${formatDecimal(perCapita)} tCO2e`}
            note={`Por persona al año ${año}.`}
          />
        </View>
      </View>
    );
  }

  if (currentScenario.name === 'Escenario Base') {
    return null;
  }

  if (mode === 'emisiones_gei') {
    const amount = Number((base - newValue).toFixed(1));
    const amountCars = Math.floor(amount * 1000000 / 560);
    const perCapita = Number((newValue / 18).toFixed(1));
    const emisionesChart = document.getElementById('emisiones');
    const canvasData = emisionesChart ? emisionesChart.getElementsByTagName('canvas')[0].toDataURL() : null;

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          <KpiCard
            label={`Reducción de emisiones GEI en ${año}`}
            value={`${formatDecimal(amount)} millones tCO2e`}
          />
          <KpiCard
            label="Emisiones GEI per cápita"
            value={`${formatDecimal(perCapita)} tCO2e`}
            note={`Por persona al año ${año}.`}
          />
          <KpiCard
            full
            label="Equivalencia referencial"
            value={`${formatInteger(amountCars)} automóviles`}
            note="Emisiones equivalentes a automóviles recorriendo la distancia por carretera entre Santiago y Coyhaique."
          />
          {typeof kpiDict.tco2_2030 === 'number' ? (
            <KpiCard
              label="Emisiones en 2030"
              value={`${formatDecimal(kpiDict.tco2_2030)} millones tCO2e`}
            />
          ) : null}
          {typeof kpiDict.tco2_2030 === 'number' ? (
            <KpiCard
              label="Límite de emisiones 2030"
              value={kpiDict.tco2_2030 > 95 ? 'Supera' : 'No supera'}
              note="Comparación con el límite de 95 millones de tCO2."
            />
          ) : null}
          {typeof kpiDict.tco2_entre_2020_2030 === 'number' ? (
            <KpiCard
              label="Emisiones acumuladas 2020-2030"
              value={`${formatDecimal(kpiDict.tco2_entre_2020_2030)} millones tCO2e`}
            />
          ) : null}
          {typeof kpiDict.tco2_entre_2020_2030 === 'number' ? (
            <KpiCard
              label="Presupuesto de emisiones 2020-2030"
              value={kpiDict.tco2_entre_2020_2030 > 1100 ? 'Supera' : 'No supera'}
              note="Comparación con el presupuesto de 1.100 millones tCO2eq."
            />
          ) : null}
        </View>
        {canvasData ? (
          <View wrap={false}>
            <Text style={styles.chartTitle}>Comparación de emisiones per cápita de Chile con el resto del mundo</Text>
            <Image src={canvasData} style={styles.chart} />
          </View>
        ) : null}
      </View>
    );
  }

  if (mode === 'demanda_electrica') {
    const targetYear = kpiDict.demanda_electrica_target_year || año;
    const baseYear = kpiDict.demanda_electrica_base_year || 2025;
    const increment = kpiDict.demanda_electrica_increment_gwh || (newValue - base);
    const percent = kpiDict.demanda_electrica_increment_percent || (base ? ((newValue - base) / base) * 100 : 0);
    const baseScenarioDifference = kpiDict.demanda_electrica_base_scenario_difference || 0;
    const baseScenarioPercentDifference = kpiDict.demanda_electrica_base_scenario_percent_difference || 0;
    const topSectors = kpiDict.demanda_electrica_top_sectores || [];

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {targetYear}</Text>
        <View style={styles.grid}>
          <KpiCard
            label={`Incremento de demanda eléctrica respecto de ${baseYear}`}
            value={`${formatInteger(increment)} GWh`}
            note={`Diferencia entre ${baseYear} y ${targetYear}.`}
          />
          <KpiCard
            label={`Incremento porcentual respecto de ${baseYear}`}
            value={`${formatInteger(percent)} %`}
            note={`Variación entre ${baseYear} y ${targetYear}.`}
          />
          <KpiCard
            label={`Diferencia respecto a Escenario Base en ${targetYear}`}
            value={`${formatInteger(baseScenarioDifference)} GWh`}
          />
          <KpiCard
            label={`Diferencia porcentual respecto a Escenario Base en ${targetYear}`}
            value={`${formatInteger(baseScenarioPercentDifference)} %`}
          />
          {topSectors.length ? (
            <KpiCard
              full
              label={`Sectores con mayor demanda eléctrica en ${targetYear}`}
              value={topSectors.map((sector, index) => `${index + 1}. ${sector.label}: ${formatInteger(sector.value)} GWh (${formatInteger(sector.share)}%)`).join('   ')}
            />
          ) : null}
        </View>
      </View>
    );
  }

  if (mode === 'demanda_energia') {
    const amount = base - newValue;
    const cars = Number(amount / 0.0082).toFixed(2);
    const homeValue = Math.floor(amount / 0.00215);
    const targetYear = kpiDict.demanda_energia_target_year || año;
    const topSectors = kpiDict.demanda_energia_top_sectores || [];

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          <KpiCard
            label={`Reducción de consumo de energía en ${año}`}
            value={`${formatInteger(amount)} TCal`}
          />
          <KpiCard
            label="Equivalencia en hogares"
            value={`${formatInteger(homeValue)} hogares`}
            note="Consumo anual de energía eléctrica equivalente."
          />
          <KpiCard
            label="Equivalencia en automóviles"
            value={`${formatInteger(cars)} automóviles`}
            note="Consumo promedio anual equivalente."
          />
          {topSectors.length ? (
            <KpiCard
              full
              label={`Sectores con mayor demanda de energía en ${targetYear}`}
              value={topSectors.map((sector, index) => `${index + 1}. ${sector.label}: ${formatInteger(sector.value)} TCal`).join('   ')}
            />
          ) : null}
        </View>
      </View>
    );
  }

  if (mode === 'demanda_energia_combustible') {
    const targetYear = kpiDict.demanda_energia_combustible_target_year || año;
    const topEnergySources = kpiDict.demanda_energia_combustible_top_energeticos || [];

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {targetYear}</Text>
        <View style={styles.grid}>
          <KpiCard
            full
            label={`Energéticos con mayor demanda de energía en ${targetYear}`}
            value={topEnergySources.length
              ? topEnergySources.map((source, index) => `${index + 1}. ${source.label}: ${formatInteger(source.value)} TCal`).join('   ')
              : 'No hay datos disponibles para calcular el ranking.'}
          />
        </View>
      </View>
    );
  }

  if (mode === 'intensidad_emisiones') {
    const value2007 = 1.02;
    const diff = value2007 - newValue;
    const perc = ((diff / value2007) * 100).toFixed(2);

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          <KpiCard
            label={`Intensidad de emisiones en ${año}`}
            value={`${Number(newValue).toLocaleString('es-ES')} ton CO2e/MM CLP`}
          />
          <KpiCard
            label="Reducción respecto de 2007"
            value={`${formatInteger(perc)} %`}
          />
        </View>
      </View>
    );
  }

  if (mode === 'generacion') {
    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          <KpiCard
            label={`Participación de energía renovable en ${año}`}
            value={`${base} %`}
          />
          <KpiCard
            label={`Participación de centrales a carbón en ${año}`}
            value={`${newValue} %`}
          />
        </View>
      </View>
    );
  }

  if (mode === 'potencia_instalada') {
    const baseYear = kpiDict.potencia_instalada_base_year || 2025;
    const rows = [
      ['Potencia instalada solar fotovoltaica', kpiDict.potencia_instalada_solar_fv, `MW en ${año}.`],
      ['Aumento solar fotovoltaica', kpiDict.potencia_instalada_incremento_solar_fv, `MW entre ${baseYear} y ${año}.`],
      ['Potencia instalada eólica', kpiDict.potencia_instalada_eolica, `MW en ${año}.`],
      ['Aumento eólica', kpiDict.potencia_instalada_incremento_eolica, `MW entre ${baseYear} y ${año}.`],
      ['Potencia instalada almacenamiento', kpiDict.potencia_instalada_almacenamiento, `MW en ${año}.`],
      ['Aumento almacenamiento', kpiDict.potencia_instalada_incremento_almacenamiento, `MW entre ${baseYear} y ${año}.`],
    ];

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          {rows.map(([label, value, note]) => (
            <KpiCard
              key={label}
              label={label}
              value={`${formatInteger(value || 0)} MW`}
              note={note}
            />
          ))}
        </View>
      </View>
    );
  }

  if (mode === 'factor_emision_red_electrica') {
    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          <KpiCard
            full
            label={`Factor de emisión de la red eléctrica en ${año}`}
            value={`${formatDecimal(newValue, 2)} tCO2/MWh`}
          />
        </View>
      </View>
    );
  }

  if (mode === 'pib') {
    const perc = (newValue - base) / base * 100;

    return (
      <View style={styles.section}>
        <Text style={styles.title}>Indicadores clave {año}</Text>
        <View style={styles.grid}>
          <KpiCard
            full
            label={`Variación del PIB en ${año}`}
            value={`${truncOneDecimal(Number(perc).toLocaleString('es-ES'))} %`}
          />
        </View>
      </View>
    );
  }

  return null;
};

export default KPIPdf;
